import { type Metric, onCLS, onFID, onINP, onLCP, onTTFB } from "web-vitals"

import { OpenAPI } from "@/client/core/OpenAPI"
import { useAuthStore } from "@/stores/auth"

const SESSION_STORAGE_KEY = "webVitals.sessionId"
const FLUSH_INTERVAL_MS = 2000

const isBrowser = typeof window !== "undefined"

const generateSessionId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID()
  }

  return `session_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

let flushTimeout: number | undefined
const metricQueue: Metric[] = []

const getSessionId = (): string => {
  if (!isBrowser) {
    return generateSessionId()
  }

  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (existing) {
      return existing
    }

    const newId = generateSessionId()
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, newId)
    return newId
  } catch {
    return generateSessionId()
  }
}

const toPayload = (metric: Metric) => ({
  name: metric.name,
  value: metric.value,
  rating: metric.rating,
  delta: metric.delta,
  id: metric.id,
  navigation_type: metric.navigationType ?? null,
  url: window.location.href,
  timestamp: new Date().toISOString(),
})

const buildEndpoint = (): string => {
  const fallbackOrigin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost"
  const base = OpenAPI.BASE || fallbackOrigin
  return new URL("/api/v1/monitoring/web-vitals", base).toString()
}

const flushQueue = async () => {
  if (!metricQueue.length) return

  const metrics = [...metricQueue]
  metricQueue.length = 0
  flushTimeout = undefined

  const payload = JSON.stringify({
    session_id: getSessionId(),
    user_id: useAuthStore.getState().user?.id ?? null,
    metrics: metrics.map(toPayload),
  })

  const endpoint = buildEndpoint()

  try {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
    ) {
      const success = navigator.sendBeacon(
        endpoint,
        new Blob([payload], { type: "application/json" }),
      )
      if (success) {
        return
      }
    }

    await fetch(endpoint, {
      method: "POST",
      body: payload,
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      credentials: "include",
    })
  } catch (error) {
    if (import.meta.env.DEV && typeof console !== "undefined") {
      console.warn("Failed to report web vitals", error)
    }
  }
}

const scheduleFlush = () => {
  if (flushTimeout) return
  if (!isBrowser) return

  flushTimeout = window.setTimeout(() => {
    void flushQueue()
  }, FLUSH_INTERVAL_MS)
}

const handleMetric = (metric: Metric) => {
  metricQueue.push(metric)
  scheduleFlush()
}

export const startWebVitalsMonitoring = () => {
  if (!isBrowser) return

  onCLS(handleMetric)
  onFID(handleMetric)
  onINP(handleMetric)
  onLCP(handleMetric)
  onTTFB(handleMetric)

  window.addEventListener("beforeunload", () => {
    void flushQueue()
  })

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        void flushQueue()
      }
    })
  }
}
